import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Heart, Gift, Users, Star, Smartphone, ArrowRight,
  TrendingUp, MessageCircle, CheckCircle2, Zap,
  BarChart3, Bell, ShieldCheck, Play, ArrowLeft, Sparkles, ChevronDown
} from 'lucide-react';
import { LeadCaptureModal } from '../../components/custom/LeadCaptureModal/LeadCaptureModal';

/* ─── Dados ───────────────────────────────────────────────── */
const stats = [
  { value: '+35%', label: 'Aumento na recorrência', icon: TrendingUp },
  { value: '10k+', label: 'Clientes ativos na plataforma', icon: Users },
  { value: '4.9★', label: 'Avaliação no app', icon: Star },
  { value: '2min', label: 'Para resgatar cashback', icon: Zap },
];

const features = [
  {
    icon: Gift,
    title: 'Cashback Real',
    desc: 'O cliente recebe cashback automático a cada abastecimento, sem cartão físico, sem burocracia. Direto no app.',
  },
  {
    icon: Smartphone,
    title: 'App White-Label',
    desc: 'Tenha sua marca no smartphone do cliente. App personalizado com logo e cores do seu posto.',
  },
  {
    icon: Bell,
    title: 'Push & Campanhas',
    desc: 'Envie notificações personalizadas com ofertas baseadas no perfil de consumo de cada cliente.',
  },
  {
    icon: BarChart3,
    title: 'Dashboard de Dados',
    desc: 'Acompanhe frequência, ticket médio e comportamento de compra de cada cliente em tempo real.',
  },
  {
    icon: ShieldCheck,
    title: 'Integrado ao PDV',
    desc: 'Resgate direto no caixa, sem validações externas. Fluxo simples para o frentista e para o cliente.',
  },
  {
    icon: MessageCircle,
    title: 'WhatsApp Automático',
    desc: 'Mensagens de aniversário, saldo de cashback e ofertas sazonais enviadas automaticamente.',
  },
];

const steps = [
  { num: '01', title: 'Cliente abastece', desc: 'No app, o cliente identifica o CPF ou escaneia o QR code no totem.' },
  { num: '02', title: 'Cashback creditado', desc: 'O valor de cashback é creditado automaticamente após a venda.' },
  { num: '03', title: 'Cliente resgata', desc: 'No próximo abastecimento, o cliente usa o saldo direto no PDV.' },
];

const faqs = [
  { q: 'Preciso ter um app próprio?', a: 'Não. A Lukos oferece um app white-label com a sua marca, configurado e publicado nas lojas sem custo adicional.' },
  { q: 'O cashback integra com o PDV?', a: 'Sim. O resgate acontece diretamente no caixa Lukos, sem etapas extras para o frentista.' },
  { q: 'Consigo ver os dados dos meus clientes?', a: 'Sim. O dashboard mostra frequência, ticket médio, dias de pico e preferências de produto de cada cliente.' },
  { q: 'Funciona para múltiplos postos?', a: 'Totalmente. Você centraliza a gestão de todos os seus postos em um único painel.' },
];

/* ─── Componente FAQ ──────────────────────────────────────── */
const FAQItem = ({ q, a }: { q: string; a: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      onClick={() => setOpen(!open)}
      className={`border rounded-2xl p-6 cursor-pointer transition-all duration-300 ${open ? 'border-pink-400 bg-pink-50' : 'border-gray-200 bg-white hover:border-pink-200'}`}
    >
      <div className="flex justify-between items-center gap-4">
        <span className="font-bold text-gray-900 text-base">{q}</span>
        <ChevronDown size={20} className={`text-pink-500 shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </div>
      {open && <p className="mt-4 text-gray-600 leading-relaxed text-sm">{a}</p>}
    </div>
  );
};

/* ─── Página Principal ────────────────────────────────────── */
const LukosFidelidadePage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-white min-h-screen font-sans overflow-x-hidden">
      <LeadCaptureModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        productName="Lukos Fidelidade"
        whatsappMessage="Olá! Gostaria de saber mais sobre o Lukos Fidelidade."
      />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-[#1a0025] via-[#2d0040] to-[#0a0010]">
        {/* Glows */}
        <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-pink-500/20 blur-[140px] rounded-full -translate-y-1/3 translate-x-1/3 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-600/20 blur-[120px] rounded-full translate-y-1/3 -translate-x-1/3 pointer-events-none" />

        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-20 w-full">
          <div className="flex flex-col lg:flex-row items-center gap-16">

            {/* Left */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="flex-1 max-w-2xl"
            >
              <Link to="/#solucoes-interativas" className="inline-flex items-center gap-2 text-white/50 hover:text-white/80 transition-colors text-sm mb-8">
                <ArrowLeft size={14} /> Voltar para soluções
              </Link>

              <div className="inline-flex items-center gap-2 bg-pink-500/15 border border-pink-500/30 rounded-full px-4 py-2 mb-8">
                <Sparkles size={14} className="text-pink-400" />
                <span className="text-pink-300 text-xs font-bold uppercase tracking-widest">Lukos Fidelidade</span>
              </div>

              <h1 className="text-6xl md:text-8xl font-black text-white leading-none tracking-tighter mb-6">
                Fidelize com<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-rose-400">
                  Cashback real
                </span>
              </h1>

              <p className="text-white/60 text-xl leading-relaxed mb-10 max-w-xl">
                Aumente o retorno dos seus clientes com um programa de fidelidade moderno. Ofereça cashback automático e campanhas personalizadas direto no app.
              </p>

              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => setIsModalOpen(true)}
                  id="btn-fidelidade-cta-hero"
                  className="inline-flex items-center gap-3 bg-pink-500 hover:bg-pink-600 text-white font-bold px-8 py-4 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-pink-500/30"
                >
                  COMEÇAR AGORA <ArrowRight size={18} />
                </button>
                <button
                  onClick={() => setIsVideoPlaying(true)}
                  className="inline-flex items-center gap-3 border border-white/20 hover:border-white/40 text-white font-bold px-8 py-4 rounded-full transition-all duration-300 hover:bg-white/5"
                >
                  <div className="w-7 h-7 bg-pink-500 rounded-full flex items-center justify-center">
                    <Play size={12} fill="white" />
                  </div>
                  Ver demonstração
                </button>
              </div>

              {/* Mini stats */}
              <div className="flex flex-wrap gap-6 mt-12 pt-12 border-t border-white/10">
                {stats.slice(0, 3).map((s, i) => (
                  <div key={i}>
                    <div className="text-2xl font-black text-white">{s.value}</div>
                    <div className="text-white/40 text-xs">{s.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right — Video/Mockup */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex-1 w-full max-w-xl"
            >
              <div className="relative rounded-[2.5rem] overflow-hidden shadow-[0_40px_120px_rgba(236,72,153,0.25)] border border-white/10 aspect-video bg-black">
                {!isVideoPlaying ? (
                  <>
                    <img
                      src="/fidelidade_customer_experience_1766006754976.png"
                      alt="Lukos Fidelidade App"
                      className="w-full h-full object-cover opacity-60"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    <button
                      onClick={() => setIsVideoPlaying(true)}
                      className="absolute inset-0 flex items-center justify-center group"
                    >
                      <div className="w-20 h-20 bg-white/10 backdrop-blur-md border border-white/20 rounded-full flex items-center justify-center group-hover:scale-110 group-hover:bg-pink-500/30 transition-all duration-300">
                        <Play size={28} fill="white" className="text-white ml-1" />
                      </div>
                    </button>
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-4 flex items-center gap-3">
                        <div className="w-10 h-10 bg-pink-500 rounded-xl flex items-center justify-center shrink-0">
                          <Heart size={18} fill="white" className="text-white" />
                        </div>
                        <div>
                          <div className="text-white font-bold text-sm">Cashback creditado!</div>
                          <div className="text-white/50 text-xs">R$ 12,50 disponíveis para resgate</div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <iframe
                    src="https://www.youtube.com/embed/F_itCCQd0nk?autoplay=1&start=32"
                    title="Lukos Fidelidade Demo"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── STATS BAR ────────────────────────────────────────── */}
      <section className="bg-pink-600 py-10">
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
          {stats.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
              <div className="text-4xl font-black mb-1">{s.value}</div>
              <div className="text-pink-100 text-sm">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── FEATURES ─────────────────────────────────────────── */}
      <section className="py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <span className="text-pink-500 font-bold uppercase tracking-widest text-sm">Recursos</span>
            <h2 className="text-5xl font-black text-gray-900 mt-3 tracking-tight">
              Tudo que o seu programa<br />
              <span className="text-pink-500">de fidelidade precisa</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-white rounded-2xl p-8 border border-gray-100 hover:border-pink-200 hover:shadow-xl hover:shadow-pink-500/5 transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-pink-50 rounded-xl flex items-center justify-center mb-5 group-hover:bg-pink-500 transition-colors duration-300">
                  <f.icon size={22} className="text-pink-500 group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-black text-gray-900 text-lg mb-3">{f.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────── */}
      <section className="py-28 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <span className="text-pink-500 font-bold uppercase tracking-widest text-sm">Como funciona</span>
            <h2 className="text-5xl font-black text-gray-900 mt-3 tracking-tight">Simples para você.<br /><span className="text-pink-500">Mágico para o cliente.</span></h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="relative text-center"
              >
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-px bg-gradient-to-r from-pink-300 to-transparent" />
                )}
                <div className="w-20 h-20 bg-gradient-to-br from-pink-500 to-rose-500 rounded-2xl flex items-center justify-center text-white text-2xl font-black mx-auto mb-6 shadow-lg shadow-pink-500/25">
                  {step.num}
                </div>
                <h3 className="font-black text-gray-900 text-xl mb-3">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SPLIT SECTION ────────────────────────────────────── */}
      <section className="py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row items-center gap-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex-1"
          >
            <div className="rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-pink-500 to-rose-600 p-1">
              <div className="rounded-[calc(1.5rem-4px)] overflow-hidden bg-white">
                <img
                  src="/customer_loyalty_app_1766005568202.png"
                  alt="Dashboard Fidelidade Lukos"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex-1"
          >
            <span className="text-pink-500 font-bold uppercase tracking-widest text-sm block mb-4">Inteligência de dados</span>
            <h2 className="text-5xl font-black text-gray-900 tracking-tight leading-none mb-6">
              Conheça seu<br />
              <span className="text-pink-500">consumidor de verdade</span>
            </h2>
            <p className="text-gray-500 text-lg leading-relaxed mb-8">
              O maior ativo do seu posto são os dados. Com o Lukos Fidelidade, você sabe quais dias o cliente mais abastece, qual produto mais compra na conveniência e age proativamente para aumentar o ticket médio.
            </p>

            <div className="space-y-4">
              {[
                'Histórico de compras por cliente',
                'Segmentação por frequência de visita',
                'Alertas de clientes inativos',
                'Relatórios exportáveis em PDF e Excel',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle2 size={20} className="text-pink-500 shrink-0" />
                  <span className="text-gray-700 font-medium">{item}</span>
                </div>
              ))}
            </div>

            <button
              onClick={() => setIsModalOpen(true)}
              id="btn-fidelidade-cta-split"
              className="mt-10 inline-flex items-center gap-3 bg-pink-500 hover:bg-pink-600 text-white font-bold px-8 py-4 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-pink-500/30"
            >
              Quero saber mais <ArrowRight size={18} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────── */}
      <section className="py-28 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <span className="text-pink-500 font-bold uppercase tracking-widest text-sm">FAQ</span>
            <h2 className="text-4xl font-black text-gray-900 mt-3">Perguntas frequentes</h2>
          </motion.div>
          <div className="space-y-4">
            {faqs.map((f, i) => <FAQItem key={i} q={f.q} a={f.a} />)}
          </div>
        </div>
      </section>

      {/* ── CTA FINAL ────────────────────────────────────────── */}
      <section className="py-28 bg-gradient-to-br from-[#1a0025] via-[#2d0040] to-[#0a0010] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-pink-500/15 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
            <div className="w-20 h-20 bg-pink-500/20 border border-pink-500/30 rounded-2xl flex items-center justify-center mx-auto mb-8">
              <Heart size={36} fill="#ec4899" className="text-pink-400" />
            </div>
            <h2 className="text-6xl md:text-7xl font-black text-white tracking-tight leading-none mb-6">
              Fidelize hoje.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-rose-400">
                Lucre sempre.
              </span>
            </h2>
            <p className="text-white/50 text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
              Transforme clientes ocasionais em verdadeiros fãs da sua marca. Comece sua estratégia de fidelização com a Lukos.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => setIsModalOpen(true)}
                id="btn-fidelidade-cta-final"
                className="inline-flex items-center gap-3 bg-pink-500 hover:bg-pink-600 text-white font-bold px-10 py-5 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-pink-500/30 text-lg"
              >
                Solicitar demonstração <ArrowRight size={20} />
              </button>
              <Link
                to="/#solucoes-interativas"
                className="inline-flex items-center gap-3 border border-white/20 hover:border-white/40 text-white font-bold px-10 py-5 rounded-full transition-all duration-300 hover:bg-white/5 text-lg"
              >
                Ver outras soluções
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default LukosFidelidadePage;
